import styled from 'styled-components';
import { Body5, Container, H5, Page } from '@ui';
import { FlatButton } from '@ui/buttons';
import React from 'react';
import { DOCUMENT_SELECTION_MODES } from '@constants';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { FormState } from '@ui/FormState';
import { Spinner } from '@ui/LoadingSpinner';
import { PROMPT_SERIES_MODES, usePromptSeries } from '@hooks/features/usePromptSeries';
import { AdvancedSettingsButton } from './AdvancedSettings';

import { includes } from '@utils/usefulFuncs';
import { DocumentIcon } from '@assets/icons/svgs/DocumentIcon';
import { CloseButton } from '@ui/buttons/CloseButton';
import { QuickLookAnalysisResult } from './components/QuickLookAnalysisResult';
import { DocumentSelectionModalControlled } from '@features/document/DocumentSelectionModal/DocumentSelectionModalControlled';

const PageWrapper = styled.div`
  + div {
    width: 100%;
  }
`;
const SubmitButtonWrapper = styled.div`
  text-align: center;
  width: 100%;
`;
const FormWrapper = styled.div`
  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }

  textarea {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-height: 100px;
    width: 100%;
    padding: 16px;
  }
`;
const SelectedDocumentsList = styled.div``;
const SelectedDocumentWrapper = styled.div`
  display: grid;
  grid-template-columns: 18px 1fr 22px;
  width: max-content;
  grid-column-gap: 12px;
  margin-bottom: 8px;

  border: 1px solid ${({ theme }) => theme.themeColors.text};
  border-radius: 4px;

  padding: 4px 8px;
`;
const DocumentIconWrapper = styled.div`
  margin-top: 3px;

  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const CloseIconWrapper = styled.div`
  svg {
    height: 13px;
    width: 13px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;

const docSelectModes = [DOCUMENT_SELECTION_MODES.allCompany, DOCUMENT_SELECTION_MODES.docType];

const SelectedDocument = ({ doc, onRemove }) => {
  return (
    <SelectedDocumentWrapper>
      <DocumentIconWrapper>
        <DocumentIcon />
      </DocumentIconWrapper>
      <Container top={2}>
        <Body5>{doc.filename}</Body5>
      </Container>
      <CloseIconWrapper>
        <CloseButton onClick={() => onRemove(doc)} size={14} />
      </CloseIconWrapper>
    </SelectedDocumentWrapper>
  );
};

export default function QuickLookTool({ onGoBack, tool }) {
  const [docs, setDocs] = React.useState([]);
  const [showDocumentSelectModal, setShowDocumentSelectModal] = React.useState(false);

  const { resultId, childRecordIds, isSubmittingToApi, formError, onSubmit, onClear, questionList } =
    usePromptSeries(tool);

  React.useEffect(() => {
    if (resultId) {
      const id = 'snapshot-answers-section';
      const yOffset = -26;
      const element = document.getElementById(id);
      if (element) {
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 300);
      }
    }
  }, [resultId]);

  const onGetResults = () => {
    onSubmit(
      null,
      PROMPT_SERIES_MODES.quickLook,
      docs.map((d) => d.id)
    );
  };

  const onSelectDocumentsClick = () => {
    setShowDocumentSelectModal(true);
  };

  const onDismissDocSelection = () => {
    setShowDocumentSelectModal(false);
  };

  const handleRemoveDocument = (fileOrFiles) => {
    if (Array.isArray(fileOrFiles)) {
      setDocs(docs.filter((d) => !includes(fileOrFiles, d.filename)));
    } else {
      setDocs(docs.filter((d) => d.filename !== fileOrFiles.filename));
    }
  };

  return (
    <PageWrapper>
      {showDocumentSelectModal && (
        <DocumentSelectionModalControlled
          documentSelectionModes={docSelectModes}
          defaultActiveMode={docSelectModes[0]}
          selectedDocs={docs}
          onSelectedDocsChange={setDocs}
          onDismiss={onDismissDocSelection}
        />
      )}
      <Page width={resultId ? '1400px' : '800px'}>
        <BackArrowButton onClick={onGoBack} />
        {isSubmittingToApi ? (
          <Container top={80} bottom={80} centerAll>
            <Container>
              <Container centerAll>
                <Spinner />
              </Container>
              <Container top={24} left={12}>
                <Body5>Working...</Body5>
              </Container>
            </Container>
          </Container>
        ) : (
          <Container top={40}>
            <FormWrapper>
              <AdvancedSettingsButton tool={tool} />
              <Container>
                <H5>Quick Look</H5>
                <Container top={4}>
                  <Body5>Select the documents to be used for the report.</Body5>
                </Container>
              </Container>
              <Container top={24}>
                <FlatButton onClick={onSelectDocumentsClick}>+ Select Documents</FlatButton>
              </Container>
              <Container top={16}>
                <SelectedDocumentsList>
                  {docs.map((d) => (
                    <SelectedDocument key={d.id} doc={d} onRemove={handleRemoveDocument} />
                  ))}
                </SelectedDocumentsList>
              </Container>
              <Container top={48}>
                <Container centerAll>
                  <FormState error={formError} />
                </Container>
                <SubmitButtonWrapper>
                  <FlatButton disabled={docs.length === 0} onClick={onGetResults} fullWidth>
                    Get Report
                  </FlatButton>
                </SubmitButtonWrapper>
              </Container>
            </FormWrapper>
          </Container>
        )}
      </Page>
      <div id={'snapshot-answers-section'} />
      {resultId && (
        <QuickLookAnalysisResult
          resultId={resultId}
          childRecordIds={childRecordIds}
          docIds={docs.map((d) => d.id)}
          sourceDocs={docs}
          questionList={questionList}
        />
      )}
    </PageWrapper>
  );
}
